import React, { useState, useEffect } from 'react';
import './AccessibilityWidget.css';
import { Settings, X, Eye, Type, Contrast, MousePointerClick, Activity } from 'lucide-react';

function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  // Settings
  const [largeText, setLargeText] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [dyslexicFont, setDyslexicFont] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [stopAnimations, setStopAnimations] = useState(false);

  useEffect(() => {
    // Check if the user has seen the popup
    const popupSeen = localStorage.getItem('a11yPopupSeen');
    if (!popupSeen) {
      setShowPopup(true);
    }
    
    // Load saved settings
    const savedSettings = JSON.parse(localStorage.getItem('a11ySettings') || '{}');
    if (savedSettings.largeText) setLargeText(true);
    if (savedSettings.highContrast) setHighContrast(true);
    if (savedSettings.dyslexicFont) setDyslexicFont(true);
    if (savedSettings.highlightLinks) setHighlightLinks(true);
    if (savedSettings.stopAnimations) setStopAnimations(true);
  }, []);

  const saveSettings = (newSettings) => {
    localStorage.setItem('a11ySettings', JSON.stringify({
      largeText, highContrast, dyslexicFont, highlightLinks, stopAnimations,
      ...newSettings
    }));
  };

  const closePopup = () => {
    setShowPopup(false);
    localStorage.setItem('a11yPopupSeen', 'true');
  };

  const toggleSetting = (setting, setter, value) => {
    const newValue = !value;
    setter(newValue);
    saveSettings({ [setting]: newValue });
  };

  // Apply classes to html element for optimal rem scaling
  useEffect(() => {
    document.documentElement.classList.toggle('a11y-large-text', largeText);
    document.documentElement.classList.toggle('a11y-high-contrast', highContrast);
    document.documentElement.classList.toggle('a11y-dyslexia', dyslexicFont);
    document.documentElement.classList.toggle('a11y-highlight-links', highlightLinks);
    document.documentElement.classList.toggle('a11y-stop-animations', stopAnimations);
  }, [largeText, highContrast, dyslexicFont, highlightLinks, stopAnimations]);

  return (
    <>
      {/* Intro Popup */}
      {showPopup && (
        <div className="a11y-popup-overlay" onClick={closePopup} role="dialog" aria-modal="true" aria-labelledby="a11y-title">
          <div className="a11y-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="a11y-close-button" onClick={closePopup} aria-label="Fermer la popup d'accessibilité">
              <X size={24} />
            </button>
            <div className="a11y-popup-icon" aria-hidden="true">
              <Settings size={40} />
            </div>
            <h2 id="a11y-title">Une expérience sur mesure.</h2>
            <p>
              CrousRadar est pensé pour s'adapter à chacun. Modifiez le contraste, la taille du texte ou la typographie pour une navigation qui correspond parfaitement à vos besoins.
            </p>
            <div className="a11y-popup-actions">
              <button 
                className="a11y-btn-primary" 
                onClick={() => { closePopup(); setIsOpen(true); }}
                aria-label="Ouvrir les paramètres d'accessibilité"
              >
                Configurer l'accessibilité
              </button>
              <button 
                className="a11y-btn-secondary" 
                onClick={closePopup}
                aria-label="Ignorer la configuration d'accessibilité pour l'instant"
              >
                Plus tard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button 
        className="a11y-floating-btn" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Ouvrir le menu d'accessibilité"
        aria-expanded={isOpen}
      >
        <Settings size={28} color="var(--text-on-primary)" />
      </button>

      {/* Settings Menu */}
      {isOpen && (
        <div className="a11y-menu-panel" role="region" aria-label="Paramètres d'accessibilité">
          <div className="a11y-menu-header">
            <h3>Accessibilité</h3>
            <button className="a11y-close-menu" onClick={() => setIsOpen(false)} aria-label="Fermer les paramètres d'accessibilité">
              <X size={20} />
            </button>
          </div>
          
          <div className="a11y-menu-body">
            <button 
              className={`a11y-toggle-btn ${largeText ? 'active' : ''}`} 
              onClick={() => toggleSetting('largeText', setLargeText, largeText)}
              aria-pressed={largeText}
            >
              <Type size={18} /> Texte plus grand
            </button>

            <button 
              className={`a11y-toggle-btn ${highContrast ? 'active' : ''}`} 
              onClick={() => toggleSetting('highContrast', setHighContrast, highContrast)}
              aria-pressed={highContrast}
            >
              <Contrast size={18} /> Contraste élevé
            </button>

            <button 
              className={`a11y-toggle-btn ${dyslexicFont ? 'active' : ''}`} 
              onClick={() => toggleSetting('dyslexicFont', setDyslexicFont, dyslexicFont)}
              aria-pressed={dyslexicFont}
            >
              <Eye size={18} /> Police pour dyslexiques
            </button>

            <button 
              className={`a11y-toggle-btn ${highlightLinks ? 'active' : ''}`} 
              onClick={() => toggleSetting('highlightLinks', setHighlightLinks, highlightLinks)}
              aria-pressed={highlightLinks}
            >
              <MousePointerClick size={18} /> Souligner les liens
            </button>

            <button 
              className={`a11y-toggle-btn ${stopAnimations ? 'active' : ''}`} 
              onClick={() => toggleSetting('stopAnimations', setStopAnimations, stopAnimations)}
              aria-pressed={stopAnimations}
            >
              <Activity size={18} /> Désactiver les animations
            </button>
            
            <button 
              className="a11y-reset-btn" 
              onClick={() => {
                setLargeText(false);
                setHighContrast(false);
                setDyslexicFont(false);
                setHighlightLinks(false);
                setStopAnimations(false);
                saveSettings({
                  largeText: false, highContrast: false, dyslexicFont: false, highlightLinks: false, stopAnimations: false
                });
              }}
              aria-label="Réinitialiser tous les paramètres d'accessibilité"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default AccessibilityWidget;
